import React, { useMemo } from 'react';
import { useContextSelector } from 'use-context-selector';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import ProductCardSkeleton from '@components/organisms/catalog/components/skeleton-components/product-card-skeleton/product-card-skeleton';
import Button from '@components/atoms/button/button';
import Usp from '@components/organisms/usp/usp';
import { CatalogProduct } from '@interfaces/models/catalogProduct';
import { CatalogContext } from '@context/catalog.context';
import useProductImpressionsTracking, {
  getImpressionTrackingIndex,
} from '@hooks/catalog-product/use-catalag-impressions-tracking';
import CategoryPageProductCard from '@components/catalog/catalog-page-product-card';
import ImpressionsTrackingWrapper from '@components/catalog/impressions-tracking-wrapper';
import useWindowSize from '@hooks/use-window-size';
import { useBucket } from '@context/bucket.context';
import isUSPBlockPositionMatched, { getMaxPdtNbPos } from '@helpers/utils/usp-block-matcher';
import dynamic from 'next/dynamic';
import useHighlightDutiesTooltip from '@hooks/catalog-product/use-highlight-duties-tooltip';
import { getValidDisplayAfterPosition, PLPInnerFeedContext } from '@context/plp-inner-feed.context';
import useRestoreCatalogScrollPosition from '@hooks/catalog-context/use-restore-catalog-scroll-position';
import CatalogMySizesModal from '@components/catalog/my-sizes/catalog-mysizes-modal/catalog-mysizes-modal';
import { InfoBox } from '@components/info-box/info-box';
import Icon from '@components/atoms/icon/icon';
import styles from './product-search/product-search.module.scss';

const HighlightDutiesTooltip = dynamic(
  () => import('@components/catalog/highlight-duties-tooltip/highlight-duties-tooltip'),
  { ssr: false },
);

const PlpBreaker = dynamic(() => import('@components/catalog/plp-breaker/plp-breaker'), { ssr: false });

const HOTFILTERS_INITIAL_RENDER_PRODUCT_COUNT = 15;
const MAX_FIRST_ROW_COUNT = 5;

const ProductSection: React.FC = (): React.JSX.Element => {
  const { t } = useTranslation();
  const shouldShowLoadingAnimation = useContextSelector(CatalogContext, (v) => v.shouldShowLoadingAnimation);
  const areMySizesEnabled = useContextSelector(CatalogContext, (v) => v.areMySizesEnabled);
  const setMySizesState = useContextSelector(CatalogContext, (v) => v.setMySizesState);
  const areMySizesApplicable = useContextSelector(CatalogContext, (v) => v.areMySizesApplicable);
  const products = useContextSelector(CatalogContext, (v) => v.products);
  const pageCount = useContextSelector(CatalogContext, (v) => v.pageCount);
  const currentPage = useContextSelector(CatalogContext, (v) => v.currentPage);
  const shouldRenderAllProducts = useContextSelector(CatalogContext, (v) => v.shouldRenderAllProducts);
  const isManualSwitchRef = useContextSelector(CatalogContext, (v) => v.isManualSwitchRef);
  const queryResult = useContextSelector(CatalogContext, (v) => v.queryResult);
  const offset = queryResult?.paginationStats?.offset ?? 0;
  const showPlpInnerFeed = useContextSelector(PLPInnerFeedContext, (v) => v.showPlpInnerFeed);
  const displayAfterPosition = useContextSelector(PLPInnerFeedContext, (v) => v.displayAfterPosition);
  const { isWiderThanMd } = useWindowSize();
  const { isFeatureEnabled } = useBucket();
  const isCatalogUspEnabled = isFeatureEnabled((f) => !!f.isCatalogUspEnabled, true);

  const shouldShowDisableMySizesPopup: boolean = areMySizesEnabled && areMySizesApplicable && pageCount === currentPage;
  const isShowingUsp = isCatalogUspEnabled && currentPage === 1 && !showPlpInnerFeed;

  const { showTooltip, setShowTooltip, onRowVisibleCallback, shouldMountDutiesTooltip } =
    useHighlightDutiesTooltip(products);
  const { updateLatestProductIndex, rowCount } = useProductImpressionsTracking({ onRowVisibleCallback });

  const productsToRender = useMemo(
    () => (shouldRenderAllProducts ? products : products.slice(0, HOTFILTERS_INITIAL_RENDER_PRODUCT_COUNT)),
    [shouldRenderAllProducts, products],
  );

  const { plpLastProductIdRef } = useRestoreCatalogScrollPosition();

  const showPlpInnerFeedOnTop = displayAfterPosition === 0 && offset === 0 && showPlpInnerFeed;

  const parsedDisplayAfterPosition = useMemo(
    () => getValidDisplayAfterPosition(displayAfterPosition, rowCount),
    [rowCount, displayAfterPosition],
  );

  return (
    <>
      <div className={clsx(styles.catalog__resultContainer)}>
        {shouldShowLoadingAnimation && (
          <ul className={styles.catalog__flexContainer}>
            {Array.from(Array(15).keys()).map((key) => (
              <li key={key}>
                <ProductCardSkeleton />
              </li>
            ))}
          </ul>
        )}
        {!shouldShowLoadingAnimation && (
          <>
            {productsToRender.length > 0 && (
              <ul className={styles.catalog__flexContainer}>
                {/*Special case when we need to show PLP Breaker at the top of the PLP*/}
                {showPlpInnerFeedOnTop && (
                  <li
                    key="plp-breaker-top"
                    className={styles.catalog__usp}
                  >
                    <PlpBreaker plpLastProductIdRef={plpLastProductIdRef} />
                  </li>
                )}
                {productsToRender.map((product: CatalogProduct, index: number) => {
                  const impressionTrackingIndex = getImpressionTrackingIndex({
                    index,
                    currentPage,
                    isWiderThanMd,
                  });
                  const isFirstItemOnRow = (impressionTrackingIndex - 1) % rowCount === 0;
                  return (
                    <React.Fragment key={product.id}>
                      {isFirstItemOnRow ? (
                        <ImpressionsTrackingWrapper
                          onVisible={updateLatestProductIndex}
                          impressionTrackingIndex={impressionTrackingIndex}
                          productsOnRow={products.slice(index, index + rowCount)}
                        >
                          <CategoryPageProductCard
                            product={product}
                            index={index}
                            disableLazyLoad={index <= MAX_FIRST_ROW_COUNT}
                            fetchpriority={index <= MAX_FIRST_ROW_COUNT ? 'high' : 'low'}
                            pageType="catalog"
                          />
                        </ImpressionsTrackingWrapper>
                      ) : (
                        <li key={`row-${product.id}`}>
                          <CategoryPageProductCard
                            product={product}
                            index={index}
                            disableLazyLoad={index <= MAX_FIRST_ROW_COUNT}
                            fetchpriority={index <= MAX_FIRST_ROW_COUNT ? 'high' : 'low'}
                            pageType="catalog"
                          />
                        </li>
                      )}
                      {index === parsedDisplayAfterPosition - offset - 1 && showPlpInnerFeed && (
                        <li
                          key={`usp-block-${product.id}`}
                          className={styles.catalog__usp}
                        >
                          <PlpBreaker plpLastProductIdRef={plpLastProductIdRef} />
                        </li>
                      )}
                      {/* USP BLOCK to be show only on the first page */}
                      {isShowingUsp && isUSPBlockPositionMatched(index, products.length, getMaxPdtNbPos(rowCount)) && (
                        <li
                          key={`usp-block-${product.id}`}
                          className={styles.catalog__usp}
                        >
                          <Usp pageType="plp" />
                        </li>
                      )}
                      {/* END USP BLOCK */}
                    </React.Fragment>
                  );
                })}
              </ul>
            )}
            {/* 'No more results for my sizes' should only appear on last page */}
            {shouldShowDisableMySizesPopup && (
              <div className={styles.catalog__resultContainer__recommend}>
                <div className={styles.catalog__resultContainer__recommend__content}>
                  <div className={clsx('vc-text-xl', styles.catalog__resultContainer__recommend__title)}>
                    {t('PERSO.MORE_ITEM.TITLE')}
                  </div>
                  <p className="vc-text-m">{t('PERSO.MORE_ITEM.SUBTITLE_ALTERNATE')}</p>
                </div>
                <Button
                  className={styles.catalog__resultContainer__recommend__button}
                  onClick={() => {
                    isManualSwitchRef.current = true;
                    setMySizesState((prev) => ({ ...prev, enabled: false }), true);
                  }}
                >
                  {t('PERSO.MORE_ITEM.CTA')}
                </Button>
              </div>
            )}
            {!shouldShowDisableMySizesPopup && products.length === 0 && (
              <div className={styles.catalog__noResultBox}>
                <InfoBox
                  variant="danger"
                  titleIcon={
                    <Icon
                      name={'high-warning-default'}
                      ariaHidden
                      className={styles.catalog__noResultBox__icon}
                    />
                  }
                  title={t('CATALOG.ERROR.TITLE')}
                  description={t('CATALOG.ERROR.DESCRIPTION')}
                >
                  <Button
                    className={styles.catalog__noResultBox__button}
                    variant="primary-white"
                    size="large"
                    fullWidth={true}
                    onClick={() => window.location.reload()}
                  >
                    <Icon
                      name="reset-large"
                      className={styles.catalog__noResultBox__button__icon}
                      ariaHidden
                    />
                    {t('CATALOG.ERROR.REFRESH_BUTTON')}
                  </Button>
                </InfoBox>
              </div>
            )}
          </>
        )}
      </div>
      {shouldMountDutiesTooltip && (
        <HighlightDutiesTooltip
          showTooltip={showTooltip}
          setShowTooltip={setShowTooltip}
        />
      )}
      <CatalogMySizesModal />
    </>
  );
};
export default ProductSection;
